import * as React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import { useNavigate, useSearchParams } from "react-router-dom";

import logo from "../../assets/img/logo.jpg";
import { API_BASE } from "../../config/api";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";

const ReservaExitosa = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const { user } = useAuth();
  const [status, setStatus] = React.useState("loading");
  const [message, setMessage] = React.useState(
    "Estamos confirmando tu reserva. Esto puede tardar unos segundos."
  );

  const paymentId =
    searchParams.get("payment_id") ||
    searchParams.get("collection_id") ||
    searchParams.get("paymentId");

  React.useEffect(() => {
    if (!paymentId) {
      setStatus("success");
      setMessage("Tu reservación se realizó correctamente.");
      return;
    }

    let isMounted = true;

    const confirmBooking = async () => {
      try {
        const response = await fetch(`${API_BASE}/booking/confirm-payment`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ paymentId }),
        });

        const data = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(
            data?.message || "No se pudo confirmar la reserva pagada"
          );
        }

        if (!isMounted) return;

        setStatus("success");
        setMessage(
          data?.created === false
            ? "Tu pago ya estaba procesado y la reserva quedó registrada correctamente."
            : "Tu reserva quedó confirmada correctamente."
        );
        if (localStorage.getItem("pendingCartCheckout") === "1") {
          clearCart();
        }
      } catch (error) {
        if (!isMounted) return;

        setStatus("error");
        setMessage(
          error.message ||
            "El pago se acreditó, pero no pudimos confirmar la reserva automáticamente."
        );
      }
    };

    confirmBooking();

    return () => {
      isMounted = false;
    };
    // clearCart no es estable entre renders y no debe reiniciar la confirmación.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId]);

  const isLoading = status === "loading";
  const isError = status === "error";
  const accentColor = isError ? "#e45b5b" : isLoading ? "#d7b06f" : "#68c28d";
  const panelBackground = isError ? "#351f20" : isLoading ? "#25231f" : "#19251f";

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        py: { xs: 3, md: 6 },
        bgcolor: "#f5f2ed",
        backgroundImage:
          "radial-gradient(circle at 10% 15%, rgba(193, 151, 91, .18), transparent 27%), radial-gradient(circle at 90% 85%, rgba(27, 45, 35, .12), transparent 29%)",
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",
            borderRadius: { xs: 3, md: 5 },
            border: "1px solid rgba(67, 55, 43, .10)",
            boxShadow: "0 28px 80px rgba(54, 43, 31, .14)",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(280px, .82fr) 1.45fr" },
          }}
        >
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              minHeight: { xs: 260, md: 520 },
              px: { xs: 3, sm: 5 },
              py: { xs: 4, md: 6 },
              color: "#fff",
              bgcolor: panelBackground,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              transition: "background-color .25s ease",
              "&::after": {
                content: '""',
                position: "absolute",
                width: 250,
                height: 250,
                borderRadius: "50%",
                right: -115,
                bottom: -105,
                border: "45px solid rgba(255,255,255,.045)",
              },
            }}
          >
            <Typography
              sx={{
                position: "relative",
                zIndex: 1,
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: "rgba(255,255,255,.65)",
              }}
            >
              Estudio Isaac
            </Typography>

            <Box sx={{ position: "relative", zIndex: 1, my: { xs: 3, md: 5 } }}>
              <Box
                sx={{
                  width: 78,
                  height: 78,
                  mb: 3,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  color: accentColor,
                  bgcolor: "rgba(255,255,255,.08)",
                  border: "1px solid rgba(255,255,255,.13)",
                }}
              >
                {isLoading ? (
                  <CircularProgress size={38} thickness={3.5} sx={{ color: accentColor }} />
                ) : isError ? (
                  <ErrorOutlineRoundedIcon sx={{ fontSize: 45 }} />
                ) : (
                  <CheckCircleRoundedIcon sx={{ fontSize: 45 }} />
                )}
              </Box>

              <Typography
                component="h1"
                sx={{
                  maxWidth: 300,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  lineHeight: 1.08,
                  fontWeight: 750,
                  letterSpacing: "-.035em",
                }}
              >
                {isLoading
                  ? "Confirmando tu reserva"
                  : isError
                  ? "Necesitamos revisar tu reserva"
                  : "¡Tu reserva está confirmada!"}
              </Typography>
            </Box>

            <Typography
              sx={{
                position: "relative",
                zIndex: 1,
                color: "rgba(255,255,255,.62)",
                fontSize: 13,
              }}
            >
              Belleza · Cuidado · Bienestar
            </Typography>
          </Box>

          <Box
            sx={{
              px: { xs: 3, sm: 5, md: 6 },
              py: { xs: 4, md: 6 },
              bgcolor: "#fff",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Logo de Estudio Isaac"
              sx={{
                width: 62,
                height: 62,
                mb: 3,
                borderRadius: "50%",
                objectFit: "cover",
                border: "1px solid #eee7df",
              }}
            />

            <Typography
              sx={{
                mb: 1.25,
                color: "#1f1b17",
                fontSize: { xs: "1.4rem", sm: "1.65rem" },
                fontWeight: 750,
                letterSpacing: "-.02em",
              }}
            >
              {isLoading
                ? "Estamos validando la operación"
                : isError
                ? "No pudimos completar la validación"
                : "Todo listo para tu próxima visita"}
            </Typography>

            <Typography
              color="text.secondary"
              sx={{ lineHeight: 1.75, fontSize: 15, mb: 3 }}
            >
              {message}
            </Typography>

            {paymentId && (
              <Box
                sx={{
                  mb: 3,
                  px: 2,
                  py: 1.5,
                  borderRadius: 2.5,
                  bgcolor: "#f8f6f2",
                  border: "1px solid #eee8df",
                }}
              >
                <Typography sx={{ fontSize: 12, color: "#81766b", mb: 0.25 }}>
                  Referencia de operación
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#302a24" }}>
                  #{paymentId}
                </Typography>
              </Box>
            )}

            <Stack spacing={2.25} sx={{ mb: 3.5 }}>
              <Box sx={{ display: "flex", gap: 1.75, alignItems: "flex-start" }}>
                <Box sx={stepIconStyles}>
                  <CalendarMonthRoundedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: "#302a24", fontSize: 14 }}>
                    Consultá todos los detalles
                  </Typography>
                  <Typography sx={{ color: "#81766b", fontSize: 13, mt: 0.25 }}>
                    {user ? "Tu fecha, horario, servicio y profesional están disponibles en Mis reservas." : "La confirmación y los avisos de tu cita se envían al WhatsApp que indicaste al reservar."}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 1.75, alignItems: "flex-start" }}>
                <Box sx={stepIconStyles}>
                  <AccessTimeRoundedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: "#302a24", fontSize: 14 }}>
                    Administrá tu turno
                  </Typography>
                  <Typography sx={{ color: "#81766b", fontSize: 13, mt: 0.25 }}>
                    {user ? "Recordá que podés reagendarlo hasta 24 horas antes de la cita." : "Para consultar o solicitar un cambio, comunicate con el salón por WhatsApp."}
                  </Typography>
                </Box>
              </Box>
            </Stack>

            <Divider sx={{ mb: 3 }} />

            {isLoading ? (
              <Typography sx={{ color: "#81766b", fontSize: 13, textAlign: "center" }}>
                No cierres esta ventana mientras terminamos.
              </Typography>
            ) : (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardRoundedIcon />}
                  onClick={() => navigate(user ? "/mis-reservas" : "/catalogo")}
                  sx={{
                    minHeight: 48,
                    flex: 1,
                    borderRadius: 2,
                    bgcolor: "#1c241f",
                    boxShadow: "none",
                    fontWeight: 700,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#303b34", boxShadow: "none" },
                  }}
                >
                  {user ? "Ver mis reservas" : "Ver catálogo"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<HomeRoundedIcon />}
                  onClick={() => navigate("/")}
                  sx={{
                    minHeight: 48,
                    borderRadius: 2,
                    px: 2.5,
                    color: "#37312b",
                    borderColor: "#d9d1c8",
                    fontWeight: 700,
                    textTransform: "none",
                    "&:hover": { borderColor: "#9f9488", bgcolor: "#faf8f5" },
                  }}
                >
                  Ir al inicio
                </Button>
              </Stack>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

const stepIconStyles = {
  flex: "0 0 auto",
  width: 38,
  height: 38,
  borderRadius: 2,
  display: "grid",
  placeItems: "center",
  bgcolor: "#f1eee9",
  color: "#3e4d44",
};

export default ReservaExitosa;
